import { keyframes } from '@emotion/react';
import type {
  ImageProps,
  SystemProps,
  SystemStyleObject,
} from '@hover/blueprint';
import {
  cssVariables,
  mapResponsive,
  transition,
  useTheme,
} from '@hover/blueprint';
import type { ResponsiveValue } from '@hover/blueprint/chakra';
import { resetFocus } from '@hover/blueprint/foundation';
import { includes } from '@hover/utilities';
import { useMemo } from 'react';

import { withHover } from 'src/shared/helpers/styles';

type NudgeSize = Parameters<(typeof cssVariables)['space']>[0];
type Direction = 'left' | 'right' | 'up' | 'down';

/**
 * Nudge an element using system spacing values. This uses `transform` so be
 * aware it will override existing transforms applied to an element.
 *
 * @param distance distance to nudge as space token
 * @param direction direction to nudge
 *
 * @returns system props to spread into element
 */
export const nudge = (
  distance: ResponsiveValue<NudgeSize>,
  direction: Direction | [Direction, Direction],
) => {
  const directions = typeof direction === 'string' ? [direction] : direction;

  return {
    transform: mapResponsive(
      space =>
        directions
          .map(d => {
            const axis = includes(['left', 'right'], d) ? 'X' : 'Y';
            const movement = includes(['left', 'up'], d) ? '-1' : '1';

            return `translate${axis}(calc(${
              cssVariables.space(space).reference
            } * ${movement}))`;
          })
          .join(' '),
      distance,
    ),
  } satisfies SystemProps;
};

/**
 * Apply styles to image `alt` text
 */
export const altStyle = (textStyle: ImageProps['textStyle'] = 'body500') =>
  ({
    color: 'transparent',
    _before: {
      textStyle,
      color: 'darkAlpha.300',
      fontFamily: 'body',
      top: '50%',
      left: '50%',
      position: 'absolute',
      transform: 'translate(-50%, -50%)',
    },
  } satisfies Partial<ImageProps>);

export const useImageFocus = (
  selector: string = 'img',
  thickness: string = '5px',
): SystemStyleObject => {
  const { colors } = useTheme();
  const childImage = `& ${selector}`;

  return {
    ...resetFocus(),
    [childImage]: {
      outline: `${thickness} solid transparent`,
      outlineOffset: `-${thickness}`,
      ...transition.easing250('outline'),
    },
    _focusVisible: {
      [childImage]: {
        outlineColor: colors.alpha(0.5, 'neutral.0'),
      },
    },
  };
};

// Re-export from shared module. Only doing this for now to prevent refactoring several file.
export { withHover };

/**
 * Disable the `translate` slide up animation on the loader in `Button` so only
 * opacity is animated
 *
 * @todo I think we should disable the translation for the `/next` theme in the
 * system, but we'll wait to see what design comes up with here
 */
export const disableLoaderSlide = {
  '& > div': { transform: 'unset !important' },
};

export interface UseBlurFadeOptions {
  /**
   * Amount of blur to apply when the element is fully visible
   */
  blur?: number;
  /**
   * Duration of the animation in seconds
   */
  duration?: number;
}

/**
 * Conditionally apply a gradual blur `backdrop-filter` to combat render
 * artifacts in browsers when an element with a `backdrop-filter` is faded in
 * and out with its `opacity`
 *
 * @param isVisible Whether the element is visible, determines whether the blur
 * animation is applied
 * @param options Optionally configure how much blur and the duration of the
 * animation
 *
 * @returns Style object to be applied via `sx`
 */
export const useBlurFade = (
  isVisible: boolean | undefined,
  options: UseBlurFadeOptions = {},
) => {
  const { blur: amount, duration } = { blur: 25, duration: 3, ...options };
  const blur = useMemo(
    () => keyframes`
      0% { backdrop-filter: blur(0px); opacity: 0; }
      10% { backdrop-filter: blur(${Math.round(amount / 4)}px); opacity: 1; }
      100% { backdrop-filter: blur(${amount}px); opacity: 1; }
    `,
    [amount],
  );

  const animation = useMemo(
    () => ({
      animationName: `${blur}`,
      animationFillMode: 'both',
      animationDuration: `${duration}s`,
      animationDelay: '0.1s',
      animationTimingFunction: 'ease-out',
    }),
    [blur, duration],
  );

  return isVisible ? animation : undefined;
};
